// Vendor Imports
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Project Imports
import { setMeasureAxisPrecision, setSecondaryMeasureAxisPrecision } from 'common/authoring_workflow/actions';
import {
  getMeasureAxisPrecision,
  getSecondaryMeasureAxisPrecision,
  isUsingPrimaryAxisOnly,
  isUsingSecondaryAxisOnly,
} from 'common/authoring_workflow/selectors/vifAuthoring';
import { AxisPrecisionSelector } from './AxisPrecisionSelector';
import Tabs from '../../shared/Tabs';
import { I18n } from 'common/i18n';
import { AuthoringWorkflowState } from 'common/authoring_workflow/reducers/types';

// Constants
import { MEASURE_AXIS_PRECISION_AUTOMATIC, MEASURE_AXIS_PRECISION_CUSTOM } from '../../../constants';

const scope = 'shared.visualizations.panes.data.fields.combo_chart_measure_axis_options';

const PRIMARY_AXIS_TAB_INDEX = 0;
const SECONDARY_AXIS_TAB_INDEX = 1;

export const AxisPrecisionSelectorDual = ({ selectedTabIndex = 0 }: { selectedTabIndex?: number }) => {
  const dispatch = useDispatch();
  const vifAuthoring = useSelector((state: AuthoringWorkflowState) => state.vifAuthoring);
  const [tabIndex, setTabIndex] = useState(selectedTabIndex);

  const isPrimaryAxis = () => tabIndex === PRIMARY_AXIS_TAB_INDEX;

  const onRadioButtonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const precision = isPrimaryAxis()
      ? getMeasureAxisPrecision(vifAuthoring)
      : getSecondaryMeasureAxisPrecision(vifAuthoring);

    const setPrecisionAction = isPrimaryAxis() ? setMeasureAxisPrecision : setSecondaryMeasureAxisPrecision;

    if (value === MEASURE_AXIS_PRECISION_AUTOMATIC) {
      dispatch(setPrecisionAction());
    } else if (value === MEASURE_AXIS_PRECISION_CUSTOM && !Number.isFinite(precision)) {
      dispatch(setPrecisionAction(0));
    }
  };

  const tabs = [
    {
      onClickTab: (index: number) => setTabIndex(index),
      selected: tabIndex === PRIMARY_AXIS_TAB_INDEX,
      tabIndex: PRIMARY_AXIS_TAB_INDEX,
      title: I18n.t('primary_axis', { scope }),
    },
    {
      onClickTab: (index: number) => setTabIndex(index),
      selected: tabIndex === SECONDARY_AXIS_TAB_INDEX,
      tabIndex: SECONDARY_AXIS_TAB_INDEX,
      title: I18n.t('secondary_axis', { scope }),
    },
  ];
  const disabled =
    (isPrimaryAxis() && isUsingSecondaryAxisOnly(vifAuthoring)) ||
    (!isPrimaryAxis() && isUsingPrimaryAxisOnly(vifAuthoring));

  const precision = isPrimaryAxis()
    ? getMeasureAxisPrecision(vifAuthoring)
    : getSecondaryMeasureAxisPrecision(vifAuthoring);

  const setPrecisionAction = isPrimaryAxis() ? setMeasureAxisPrecision : setSecondaryMeasureAxisPrecision;

  const onSliderChange = (precisionValue?: number) => {
    dispatch(setPrecisionAction(precisionValue));
  };

  return (
    <div className="measure-axis-precision-dual-container">
      <Tabs tabs={tabs} />
      <AxisPrecisionSelector
        disabled={disabled}
        key={tabIndex}
        onRadioButtonChange={onRadioButtonChange}
        onSliderChange={onSliderChange}
        precision={precision}
      />
    </div>
  );
};
